<script lang="ts">
  /**
   * A document, as Pattern 2's object page — `03-shell-and-layout.md`.
   *
   *   breadcrumb
   *   HERO      the document itself, one number, status chip
   *   FACTS     kind · expiry · amount · supplier · linked asset
   *   LINKED    the renewal chain and the estate record
   *   ACTIONS   open · correct
   *   HISTORY   every access, as sentences, newest first
   *
   * ## Correcting happens here now, not in the list
   *
   * The list used to edit in place, on the argument that you correct a document
   * *because* you are looking at it in the list. That is true and this is still
   * better: the cross-checks point at individual fields, and a mark saying "the
   * amount does not match" is only useful beside the amount. The list keeps a count
   * so nothing is hidden; the sentence lives where the field is.
   *
   * ## Why the hero is not a preview of the file
   *
   * Every open of a document is written to the audit trail, and rendering a preview
   * would fetch the file on page load — filling the trail with opens nobody
   * performed and making "who looked at the title deed" unanswerable. So the hero
   * is the neutral ground `03-shell-and-layout.md` asks for: a labelled slot naming
   * what the file is, with opening it as a deliberate press.
   */
  import { Icon, dateZA, zar, DASH } from "@steyncd/steyn-design";
  import { Panel } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { expiryOf, pretty, type Check, type DocDetail, type Meta, type TrailEntry } from "../lib/model";

  const {
    id,
    meta,
    onback,
    onopenDoc,
    onchanged,
  }: {
    id: string;
    meta: Meta | null;
    onback: () => void;
    onopenDoc: (id: string) => void;
    /** Something moved between tabs — the lists behind this page are now stale. */
    onchanged: () => void;
  } = $props();

  let doc = $state<DocDetail | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let trail = $state<TrailEntry[] | null>(null);
  let trailError = $state<string | null>(null);

  let opening = $state(false);
  let openError = $state<string | null>(null);

  async function load() {
    loading = true;
    try {
      doc = await api.get<DocDetail>(`/docs/${id}`);
      error = null;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not read that document.";
    } finally {
      loading = false;
    }
    try {
      trail = (await api.get<{ entries: TrailEntry[] }>(`/docs/${id}/history`)).entries;
      trailError = null;
    } catch (e) {
      // The history failing must not take the document with it. A page that shows
      // the facts and says the trail is unreadable is worth more than an error card.
      trail = null;
      trailError = e instanceof ApiError ? e.message : "Could not read the history.";
    }
  }

  // Keyed on `id` so following a link from one document to another reloads rather
  // than showing the previous one's facts under the new one's name.
  $effect(() => {
    void id;
    void load();
  });

  const exp = $derived(expiryOf(doc?.expiresAt));

  /** The check that failed on a given field, if any. Marks sit beside their field. */
  function checkOn(field: string): Check | null {
    return doc?.checks.find((c) => c.field === field) ?? null;
  }
  /** Checks with no field — older entries filed before checks carried one. */
  const looseChecks = $derived(doc?.checks.filter((c) => !c.field) ?? []);

  async function openFile() {
    if (!doc?.hasFile) return;
    opening = true;
    openError = null;
    try {
      const r = await api.get<{ url: string }>(`/docs/${doc.id}/file`);
      window.open(r.url, "_blank", "noopener");
      // The open has just been recorded server-side; refresh so the trail below
      // shows it rather than going stale the moment it becomes interesting.
      trail = (await api.get<{ entries: TrailEntry[] }>(`/docs/${doc.id}/history`)).entries;
    } catch (e) {
      openError = e instanceof ApiError ? e.message : "Could not open it.";
    } finally {
      opening = false;
    }
  }

  // ── correcting ────────────────────────────────────────────────────────────
  type Draft = {
    title: string;
    docType: string;
    supplier: string;
    policyNumber: string;
    amount: string;
    documentDate: string;
    expiresAt: string;
    expiryKind: string;
    notes: string;
    inCase: boolean;
    notifyOnExpiry: boolean;
  };
  let draft = $state<Draft | null>(null);
  let saving = $state(false);
  let saveErr = $state<string | null>(null);

  function startEdit() {
    if (!doc) return;
    saveErr = null;
    draft = {
      title: doc.title,
      docType: doc.docType,
      supplier: doc.supplier ?? "",
      policyNumber: doc.policyNumber ?? "",
      amount: doc.amount == null ? "" : String(doc.amount),
      // <input type="date"> speaks yyyy-mm-dd only; anything else renders blank and
      // silently discards the value the document already had.
      documentDate: (doc.documentDate ?? "").slice(0, 10),
      expiresAt: (doc.expiresAt ?? "").slice(0, 10),
      expiryKind: doc.expiryKind ?? "",
      notes: doc.notes ?? "",
      inCase: doc.inCase,
      notifyOnExpiry: doc.notifyOnExpiry,
    };
  }

  /**
   * Mark it expired now, rather than making somebody work out that "expired" means
   * typing yesterday's date. That kind of small indirection is how a field stops
   * being touched, and then the expiring list stops being true.
   */
  function expireNow() {
    if (draft) draft.expiresAt = new Date().toISOString().slice(0, 10);
  }

  async function save() {
    if (!draft || !doc) return;
    if (!draft.title.trim()) {
      saveErr = "A title is required.";
      return;
    }
    saving = true;
    saveErr = null;
    try {
      // Every field is sent, including cleared ones: `undefined` means "leave
      // alone" to the server, so omitting an emptied field would make clearing a
      // value impossible — the edit most likely to be wanted after a bad guess.
      await api.patch(`/docs/${doc.id}`, {
        title: draft.title.trim(),
        docType: draft.docType,
        supplier: draft.supplier.trim() || null,
        policyNumber: draft.policyNumber.trim() || null,
        amount: draft.amount.trim() === "" ? null : Number(draft.amount),
        documentDate: draft.documentDate || null,
        expiresAt: draft.expiresAt || null,
        expiryKind: draft.expiresAt ? draft.expiryKind || null : null,
        notes: draft.notes.trim() || null,
        inCase: draft.inCase,
        notifyOnExpiry: draft.notifyOnExpiry,
      });
      draft = null;
      await load();
      onchanged();
    } catch (e) {
      saveErr = e instanceof ApiError ? e.message : "Could not save that.";
    } finally {
      saving = false;
    }
  }

  // ── the history, as sentences ─────────────────────────────────────────────
  /** Field names as words, so an edit reads as a sentence rather than a schema. */
  const FIELD_WORDS: Record<string, string> = {
    title: "the name",
    docType: "the type",
    supplier: "the supplier",
    policyNumber: "the number",
    amount: "the amount",
    documentDate: "the date on it",
    expiresAt: "the expiry date",
    expiryKind: "the kind of expiry",
    notes: "the notes",
    inCase: "whether it is in the in-case folder",
    notifyOnExpiry: "whether it reminds you",
    nudged: "the reminder schedule",
  };

  function listWords(fields: string[]): string {
    const words = fields.map((f) => FIELD_WORDS[f] ?? f).filter((w, i, a) => a.indexOf(w) === i);
    if (words.length === 0) return "something";
    if (words.length === 1) return words[0]!;
    return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
  }

  /** Who, in the form a household reads — the local part of an address, not the address. */
  function who(by: string): string {
    const name = by.includes("@") ? by.split("@")[0]! : by;
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  function sentence(e: TrailEntry): string {
    switch (e.kind) {
      case "vault.doc.filed":
        return `${who(e.by)} filed it.`;
      case "vault.doc.edited":
        return `${who(e.by)} corrected ${listWords(e.fields)}.`;
      case "vault.incase.open":
        return `${who(e.by)} opened it from the in-case folder.`;
      default:
        return `${who(e.by)} opened it.`;
    }
  }
</script>

<nav class="crumb" aria-label="Breadcrumb">
  <button class="crumb__back" onclick={onback}>Library</button>
  <Icon name="chevron-right" size={12} />
  <span class="crumb__here">{doc?.title ?? "Document"}</span>
</nav>

<Panel {loading} {error} isEmpty={!loading && !error && !doc} onretry={() => void load()} variant="card" rows={2}>
  {#snippet empty()}
    That document is not in Vault any more. Go back to the library and search for it by name —
    a renewed document keeps the old one linked rather than replacing it.
  {/snippet}

  {#if doc}
    <div class="obj">
      <div class="obj__main">
        <!-- ── Hero ─────────────────────────────────────────────────────── -->
        <section class="hero">
          <div class="hero__slot" aria-hidden="true">
            <Icon name={doc.inCase ? "shield" : "file"} size={26} />
            <span class="hero__slotLabel">
              {#if doc.hasFile}
                {doc.mime ?? "file"}{doc.sizeBytes ? ` · ${Math.max(1, Math.round(doc.sizeBytes / 1024))} kB` : ""}
              {:else}
                Record only — no scan
              {/if}
            </span>
          </div>

          <div class="hero__body">
            <div class="hero__chips">
              {#if doc.inCase}
                <span class="chip chip--acc"><Icon name="shield" size={11} /> In case</span>
              {/if}
              {#if exp?.tone === "warn"}
                <span class="chip chip--warn">
                  <Icon name={exp.glyph ?? "alert"} size={11} />
                  {exp.word}
                </span>
              {/if}
              {#if doc.replacedBy}
                <span class="chip"><Icon name="pause" size={11} /> Replaced</span>
              {/if}
              {#if !doc.hasFile}
                <span class="chip"><Icon name="info" size={11} /> No scan</span>
              {/if}
            </div>

            <h1 class="hero__t">{doc.title}</h1>
            <p class="hero__prov">
              {[pretty(doc.docType), doc.supplier, doc.documentDate ? `dated ${dateZA(doc.documentDate)}` : null]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>

          <!-- One number, and only one. The rest are facts and live in the spine. -->
          <div class="hero__fig">
            {#if exp}
              <span class="fig" class:fig--warn={exp.tone === "warn"}>
                {Math.abs(exp.days)}<span class="fig__u"> days</span>
              </span>
              <span class="hero__figLabel">{exp.days < 0 ? "since it expired" : "until it expires"}</span>
            {:else if doc.amount != null}
              <span class="fig">{zar(doc.amount)}</span>
              <span class="hero__figLabel">on this document</span>
            {:else}
              <!-- Em dash, never a zero and never a blank. This document carries no
                   deadline and no amount, and saying so is the honest figure. -->
              <span class="fig">{DASH}</span>
              <span class="hero__figLabel">no deadline, no amount</span>
            {/if}
          </div>
        </section>

        <!-- ── Actions ──────────────────────────────────────────────────── -->
        <div class="acts acts--obj">
          {#if doc.hasFile}
            <button class="btn-primary" onclick={openFile} disabled={opening}>
              {opening ? "Opening…" : "Open the document"}
            </button>
          {:else}
            <span class="sub">No file is attached — this is a record of where the original is.</span>
          {/if}
          <button class="lnk" onclick={() => (draft ? (draft = null) : startEdit())}>
            {draft ? "Stop correcting" : "Correct it"}
          </button>
        </div>

        {#if openError}
          <p class="err"><Icon name="alert" size={12} /> {openError}</p>
        {/if}

        {#if looseChecks.length > 0}
          <div class="attn attn--warn">
            <span class="attn__badge" aria-hidden="true"><Icon name="alert" size={12} /></span>
            <span class="attn__text">
              {#each looseChecks as c (c.says)}<span class="loose">{c.says}</span>{/each}
            </span>
          </div>
        {/if}

        <!-- ── Correcting ───────────────────────────────────────────────── -->
        {#if draft}
          <section class="edit">
            <div class="grid">
              <label class="wide">
                <span>Name</span>
                <input bind:value={draft.title} />
              </label>
              <label>
                <span>Type</span>
                <select
                  bind:value={draft.docType}
                  onchange={() => {
                    // Offer the expiry kind that matches the new type, but only when
                    // nobody has already chosen one — overwriting a deliberate choice
                    // with a guess is worse than not suggesting at all.
                    const sug = meta?.suggested?.[draft!.docType];
                    if (sug && !draft!.expiryKind) draft!.expiryKind = sug;
                  }}
                >
                  {#each meta?.docTypes ?? [doc.docType] as t (t)}
                    <option value={t}>{pretty(t)}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>Supplier or issuer</span>
                <input bind:value={draft.supplier} />
              </label>
              <label>
                <span>Policy number <span class="sub">(or account / membership)</span></span>
                <input bind:value={draft.policyNumber} autocomplete="off" spellcheck="false" />
              </label>
              <label>
                <span>Amount (R)</span>
                <input type="number" step="0.01" bind:value={draft.amount} />
              </label>
              <label>
                <span>Date on it</span>
                <input type="date" bind:value={draft.documentDate} />
              </label>
              <label>
                <span>Expires <span class="sub">(blank if never)</span></span>
                <input type="date" bind:value={draft.expiresAt} />
              </label>
              <label>
                <span>Expiry kind</span>
                <select bind:value={draft.expiryKind} disabled={!draft.expiresAt}>
                  <option value="">{DASH}</option>
                  {#each meta?.expiryKinds ?? [] as k (k)}
                    <option value={k}>{pretty(k)}</option>
                  {/each}
                </select>
              </label>
              <label class="wide">
                <span>Notes</span>
                <input bind:value={draft.notes} placeholder="Anything you would want to read in a year" />
              </label>
            </div>

            <label class="chk">
              <input type="checkbox" bind:checked={draft.inCase} />
              <span>Put it in the <strong>in case</strong> folder</span>
            </label>

            <!-- The expiry date and the decision to be told about it are separate
                 facts. A benefit summary and a bank statement both carry an end date
                 nobody can act on, and putting those on the reminder list is how a
                 reminder list stops being read — which then costs you the licence
                 disc it exists for. -->
            <label class="chk">
              <input type="checkbox" bind:checked={draft.notifyOnExpiry} disabled={!draft.expiresAt} />
              <span>
                Remind me before this expires
                {#if !draft.expiresAt}<span class="sub">— needs an expiry date</span>{/if}
              </span>
            </label>

            {#if saveErr}
              <p class="err"><Icon name="alert" size={12} /> {saveErr}</p>
            {/if}

            <div class="acts">
              <button class="lnk" onclick={expireNow} disabled={saving}>Mark expired today</button>
              <button class="lnk" onclick={() => (draft = null)} disabled={saving}>Cancel</button>
              <button class="btn-primary" onclick={save} disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </section>
        {/if}

        <!-- ── History ──────────────────────────────────────────────────── -->
        <section class="hist">
          <h2 class="divider">History</h2>
          {#if trailError}
            <p class="err"><Icon name="alert" size={12} /> {trailError} The document itself is fine.</p>
          {:else if trail === null}
            <p class="sub">Reading the trail…</p>
          {:else if trail.length === 0}
            <p class="sub">
              Nothing recorded yet. Every open, correction and filing from here on is written down —
              which is what makes the in-case folder safe to leave open.
            </p>
          {:else}
            <ol class="hist__list">
              {#each trail as e, i (e.at + e.kind + i)}
                <li class="hist__row">
                  <span class="hist__dot" class:hist__dot--case={e.kind === "vault.incase.open"} aria-hidden="true">
                    <Icon name="dot" size={9} />
                  </span>
                  <span class="hist__t">{sentence(e)}</span>
                  <span class="hist__at">{dateZA(e.at)}</span>
                </li>
              {/each}
            </ol>
          {/if}
        </section>
      </div>

      <!-- ── Facts spine ───────────────────────────────────────────────── -->
      <aside class="spine">
        <h2 class="divider">Facts</h2>
        <dl class="spine__dl">
          {@render fact("Kind", pretty(doc.docType), null)}
          {@render fact(
            "Expires",
            exp ? `${dateZA(doc.expiresAt)} — ${exp.word}` : DASH,
            checkOn("expiresAt"),
            exp?.tone === "warn",
          )}
          {@render fact("Amount", doc.amount == null ? DASH : zar(doc.amount), checkOn("amount"))}
          {@render fact("Supplier", doc.supplier ?? DASH, checkOn("supplier"))}
          <!-- Linked asset: Homestead's register is not wired to Vault yet, so this
               renders an em dash and says so. Inventing a plausible asset name here
               would be the one lie this whole spine exists to prevent. -->
          {@render fact("Linked asset", DASH, null)}
          <dd class="spine__note">Homestead linking is not built yet — nothing is being hidden.</dd>

          {@render fact("Number", doc.policyNumber ?? DASH, null)}
          {@render fact("Dated", doc.documentDate ? dateZA(doc.documentDate) : DASH, checkOn("documentDate"))}
          {@render fact("Tax year", doc.taxYear ?? DASH, null)}
          {@render fact("Reminders", doc.expiresAt ? (doc.notifyOnExpiry ? "On" : "Off") : DASH, null)}
        </dl>

        {#if doc.notes}
          <h2 class="divider">Note</h2>
          <p class="spine__notes">{doc.notes}</p>
        {/if}

        <h2 class="divider">Linked</h2>
        <div class="spine__linked">
          {#if doc.replaces && doc.replacesTitle}
            <!-- Captured into a const rather than dereferenced inside the handler:
                 the narrowing from the `{#if}` does not survive into a closure. -->
            {@const prevId = doc.replaces}
            <button class="lnk lnk--block" onclick={() => onopenDoc(prevId)}>
              <Icon name="chevron-up" size={12} /> Replaces {doc.replacesTitle}
            </button>
          {/if}
          {#if doc.replacedBy && doc.replacedByTitle}
            {@const nextId = doc.replacedBy}
            <button class="lnk lnk--block" onclick={() => onopenDoc(nextId)}>
              <Icon name="chevron-down" size={12} /> Replaced by {doc.replacedByTitle}
            </button>
          {/if}
          {#if doc.estate}
            <div class="estate">
              <span class="estate__k">Signed original</span>
              <span class="estate__v" class:estate__v--missing={!doc.estate.originalLocation}>
                {doc.estate.originalLocation ?? "not recorded"}
              </span>
              {#if doc.estate.contact}
                <span class="estate__k">Who to call</span>
                <span class="estate__v">
                  {doc.estate.contact.name}{doc.estate.contact.role ? `, ${doc.estate.contact.role}` : ""}
                  {#if doc.estate.contact.phone}<span class="num"> · {doc.estate.contact.phone}</span>{/if}
                </span>
              {/if}
            </div>
          {/if}
          {#if !doc.replaces && !doc.replacedBy && !doc.estate}
            <p class="sub">
              Nothing links to this yet. Renewing a document links the old one to the new one
              automatically.
            </p>
          {/if}
        </div>
      </aside>
    </div>
  {/if}
</Panel>

{#snippet fact(label: string, value: string, check: Check | null, warn = false)}
  <dt class="spine__k">{label}</dt>
  <dd class="spine__v" class:spine__v--warn={warn}>
    {value}
    {#if check}
      <!-- The confidence mark. Amber glyph, and the check that failed **in words**
           right beside it. Never a percentage: a score nobody can interpret turns a
           correctable fact into an argument about a number. -->
      <span class="spine__check">
        <Icon name="alert" size={11} />
        {check.says}
      </span>
    {/if}
  </dd>
{/snippet}

<style>
  .crumb {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: var(--sp-3);
    font-size: var(--fs-small);
    color: var(--mut);
  }
  .crumb__back {
    font-size: var(--fs-small);
    font-weight: 700;
    color: var(--mut);
  }
  .crumb__back:hover {
    color: var(--acc);
  }
  .crumb__here {
    color: var(--tx2);
  }

  .obj {
    display: flex;
    align-items: flex-start;
    gap: var(--gap-section);
  }
  .obj__main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--sp-4);
  }

  /* ── Hero ──────────────────────────────────────────────────────────────── */
  .hero {
    display: flex;
    align-items: center;
    gap: var(--sp-4);
    padding: var(--sp-4);
    border-radius: var(--r-surface);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  /* A neutral ground with a labelled slot, not a preview — see the file header. */
  .hero__slot {
    flex-shrink: 0;
    width: 84px;
    height: 104px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: var(--sp-2);
    text-align: center;
    border-radius: var(--r-control);
    background: var(--s2);
    box-shadow: inset 0 0 0 1px var(--line);
    color: var(--mut);
  }
  .hero__slotLabel {
    font-size: var(--fs-micro);
    line-height: 1.3;
    word-break: break-word;
  }
  .hero__body {
    flex: 1;
    min-width: 0;
  }
  .hero__chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: var(--r-pill);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  /* Colour plus glyph plus word, in every one of these. */
  .chip--warn {
    color: var(--warn);
    box-shadow: inset 0 0 0 1px var(--warn);
  }
  .chip--acc {
    color: var(--acc);
    box-shadow: inset 0 0 0 1px var(--acc);
  }
  .hero__t {
    margin: 0;
    font-size: var(--fs-h2);
    font-weight: 600;
    line-height: 1.2;
    color: var(--tx);
  }
  .hero__prov {
    margin: 4px 0 0;
    font-size: var(--fs-small);
    color: var(--mut);
  }
  .hero__fig {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    text-align: right;
  }
  .fig--warn {
    color: var(--warn);
  }
  .hero__figLabel {
    font-size: var(--fs-micro);
    color: var(--mut);
  }

  .acts--obj {
    justify-content: flex-start;
    padding-top: 0;
  }
  .err {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 0;
    font-size: var(--fs-small);
    font-weight: 700;
    color: var(--warn);
  }
  .loose {
    display: block;
  }

  .edit {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
    padding: var(--sp-4);
    border-radius: var(--r-surface);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .chk {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    font-size: var(--fs-small);
    color: var(--tx2);
  }

  /* ── History ───────────────────────────────────────────────────────────── */
  .hist__list {
    list-style: none;
    margin: var(--sp-2) 0 0;
    padding: 0;
  }
  .hist__row {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    min-height: var(--row-h);
    padding: var(--sp-1) 0;
    border-bottom: 1px solid var(--line);
    font-size: var(--fs-small);
    color: var(--tx2);
  }
  .hist__row:last-child {
    border-bottom: none;
  }
  .hist__dot {
    color: var(--mut);
    display: grid;
    place-items: center;
  }
  /* An in-case open is the entry somebody may one day need to point at, so it is
     marked — amber dot plus the sentence that already says "from the in-case
     folder", never the colour on its own. */
  .hist__dot--case {
    color: var(--warn);
  }
  .hist__t {
    flex: 1;
    min-width: 0;
  }
  .hist__at {
    flex-shrink: 0;
    font-size: var(--fs-micro);
    color: var(--mut);
    font-variant-numeric: tabular-nums;
  }

  /* ── Facts spine ───────────────────────────────────────────────────────── */
  .spine {
    flex: 0 0 clamp(240px, 30%, 320px);
    padding: var(--sp-4);
    border-radius: var(--r-surface);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .spine__dl {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 6px var(--sp-3);
    margin: var(--sp-2) 0 var(--sp-4);
  }
  .spine__k {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  /* Right-set, as Pattern 2 draws it — the values line up and the eye runs down
     them instead of hunting along ragged label widths. */
  .spine__v {
    margin: 0;
    font-size: var(--fs-small);
    color: var(--tx);
    text-align: right;
  }
  .spine__v--warn {
    color: var(--warn);
    font-weight: 700;
  }
  .spine__check {
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 3px;
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--warn);
    text-align: right;
    line-height: 1.35;
  }
  .spine__note {
    grid-column: 1 / -1;
    margin: 0;
    font-size: var(--fs-micro);
    color: var(--mut);
  }
  .spine__notes {
    margin: var(--sp-2) 0 var(--sp-4);
    font-size: var(--fs-small);
    color: var(--tx2);
    line-height: 1.5;
  }
  .spine__linked {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: var(--sp-2);
  }
  .lnk--block {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    text-align: left;
    color: var(--tx2);
  }
  .estate {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 4px var(--sp-3);
    font-size: var(--fs-small);
  }
  .estate__k {
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
  }
  .estate__v {
    color: var(--tx2);
    text-align: right;
  }
  .estate__v--missing {
    color: var(--warn);
    font-weight: 700;
  }

  @container panel (max-width: 560px) {
    .obj {
      flex-direction: column;
    }
    .spine {
      flex: 1 1 auto;
      width: 100%;
    }
    .hero {
      flex-wrap: wrap;
    }
    .hero__fig {
      align-items: flex-start;
      text-align: left;
    }
  }
</style>
